import { create } from 'zustand';
import { loadCitas, loadLecturas, loadEventos, saveCitas, saveLecturas, saveEventos } from '@/utils/storage';
import { getLecturas, getCitas, getEventos } from '@/utils/api';
import storageService from '../services/storageService';

/**
 * Store principal del calendario usando Zustand
 */
export const useCalendarStore = create((set, get) => ({
  // Estado
  currentDate: new Date(),
  view: 'month', // 'day', 'month', 'year'
  citas: [],
  lecturas: [],
  eventos: [],
  loading: false,
  useBackend: true, // Usar backend API o localStorage
  settings: {
    defaultCalendar: 'hebrew',
    showDualCalendar: true,
    hebrewDayStart: '18:00',
    hebrewDayEnd: '18:00',
    timezone: 'America/New_York',
    locale: 'es',
    firstDayOfWeek: 0,
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    enabledViews: ['day', 'month', 'year'],
    defaultView: 'month',
  },
  theme: {
    colors: {
      primary: '#4A90E2',
      secondary: '#E24A90',
      accent: '#90E24A',
    },
  },
  // Filtros
  filters: {
    showCitas: true,
    showLecturas: true,
    showEventos: true,
    showFestividadesJudias: false, // Festividades automáticas de @hebcal/core (desactivado por defecto)
    tiposLectura: [], // Vacío = mostrar todos. ['Torah', 'Profetas', 'Lectura Diaria', etc]
    tiposCita: [], // Filtros por tipo de cita
    tiposEvento: [], // Filtros por tipo de evento
  },
  
  // Acciones - Navegación
  setCurrentDate: (date) => set({ currentDate: date }),
  setView: (view) => set({ view }),
  goToToday: () => set({ currentDate: new Date() }),
  goToPreviousDay: () => {
    const { currentDate } = get();
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    set({ currentDate: newDate });
  },
  goToNextDay: () => {
    const { currentDate } = get();
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    set({ currentDate: newDate });
  },
  goToPreviousMonth: () => {
    const { currentDate } = get();
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    set({ currentDate: newDate });
  },
  goToNextMonth: () => {
    const { currentDate } = get();
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    set({ currentDate: newDate });
  },
  goToPreviousWeek: () => {
    const { currentDate } = get();
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    set({ currentDate: newDate });
  },
  goToNextWeek: () => {
    const { currentDate } = get();
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    set({ currentDate: newDate });
  },
  goToPreviousYear: () => {
    const { currentDate } = get();
    const newDate = new Date(currentDate);
    newDate.setFullYear(newDate.getFullYear() - 1);
    set({ currentDate: newDate });
  },
  goToNextYear: () => {
    const { currentDate } = get();
    const newDate = new Date(currentDate);
    newDate.setFullYear(newDate.getFullYear() + 1);
    set({ currentDate: newDate });
  },
  
  // Acciones - Citas
  loadCitas: () => {
    const citas = loadCitas();
    set({ citas });
  },
  addCita: (cita) => {
    const { citas } = get();
    const newCitas = [...citas, cita];
    set({ citas: newCitas });
    saveCitas(newCitas);
  },
  updateCita: (id, updatedCita) => {
    const { citas } = get();
    const newCitas = citas.map(c => c.id === id ? { ...c, ...updatedCita } : c);
    set({ citas: newCitas });
    saveCitas(newCitas);
  },
  deleteCita: (id) => {
    const { citas } = get();
    const newCitas = citas.filter(c => c.id !== id);
    set({ citas: newCitas });
    saveCitas(newCitas);
  },
  
  // Acciones - Lecturas
  loadLecturas: async () => {
    console.log('🏪 [STORE] 📖 Iniciando loadLecturas...');
    const { useBackend } = get();
    console.log('🏪 [STORE] useBackend:', useBackend);
    
    set({ loading: true });
    try {
      if (useBackend) {
        // Cargar desde API
        console.log('🏪 [STORE] 🌐 Intentando cargar desde API...');
        const lecturas = await getLecturas();
        console.log('🏪 [STORE] ✅ Lecturas cargadas desde API:', lecturas.length);
        set({ lecturas, loading: false });
        console.log('🏪 [STORE] 💾 Estado actualizado con', lecturas.length, 'lecturas');
      } else {
        // Cargar desde localStorage
        console.log('🏪 [STORE] 💾 Cargando desde localStorage...');
        const lecturas = loadLecturas();
        console.log('🏪 [STORE] ✅ Lecturas cargadas desde localStorage:', lecturas.length);
        set({ lecturas, loading: false });
      }
    } catch (error) {
      console.error('🏪 [STORE] ❌ Error cargando lecturas:', error);
      // Fallback a localStorage si falla el backend
      console.log('🏪 [STORE] 🔄 Intentando fallback a localStorage...');
      const lecturas = loadLecturas();
      console.log('🏪 [STORE] 💾 Fallback completado:', lecturas.length, 'lecturas');
      set({ lecturas, loading: false, useBackend: false });
    }
  },
  addLectura: (lectura) => {
    const { lecturas } = get();
    const newLecturas = [...lecturas, lectura];
    set({ lecturas: newLecturas });
    saveLecturas(newLecturas);
  },
  updateLectura: (id, updatedLectura) => {
    const { lecturas } = get();
    const newLecturas = lecturas.map(l => l.id === id ? { ...l, ...updatedLectura } : l);
    set({ lecturas: newLecturas });
    saveLecturas(newLecturas);
  },
  deleteLectura: (id) => {
    const { lecturas } = get();
    const newLecturas = lecturas.filter(l => l.id !== id);
    set({ lecturas: newLecturas });
    saveLecturas(newLecturas);
  },
  
  // Acciones - Eventos
  loadEventos: async () => {
    console.log('🏪 [STORE] ⭐ Iniciando loadEventos...');
    try {
      // 1. Cargar eventos del backend (localStorage actual)
      const eventosBackend = loadEventos();
      console.log('🏪 [STORE] 🌐 Eventos del backend:', eventosBackend.length);
      
      // 2. Intentar cargar eventos locales de IndexedDB
      let eventosLocales = [];
      try {
        await storageService.init();
        eventosLocales = await storageService.eventos.obtenerTodos();
        console.log('🏪 [STORE] 💾 Eventos locales (IndexedDB):', eventosLocales.length);
        
        // Marcar eventos locales para distinguirlos visualmente
        eventosLocales = eventosLocales.map(e => ({
          ...e,
          id: `local_${e.id}`, // Prefijo para identificar eventos locales
          localId: e.id, // Guardar ID original
          esLocal: true, // Flag para identificar
          color: e.color || '#9333EA' // Color púrpura para eventos locales
        }));
      } catch (error) {
        console.warn('🏪 [STORE] ⚠️ No se pudieron cargar eventos locales:', error.message);
      }
      
      // 3. Combinar ambas fuentes
      const todosLosEventos = [...eventosBackend, ...eventosLocales];
      console.log('🏪 [STORE] ✅ Total eventos combinados:', todosLosEventos.length);
      
      set({ eventos: todosLosEventos });
    } catch (error) {
      console.error('🏪 [STORE] ❌ Error cargando eventos:', error);
      // Fallback: solo eventos del backend
      const eventos = loadEventos();
      set({ eventos });
    }
  },
  addEvento: (evento) => {
    const { eventos } = get();
    const newEventos = [...eventos, evento];
    set({ eventos: newEventos });
    saveEventos(newEventos);
  },
  updateEvento: async (id, updatedEvento) => {
    const { eventos } = get();
    
    // Verificar si es un evento local
    if (id && id.toString().startsWith('local_')) {
      const localId = updatedEvento.localId || parseInt(id.toString().replace('local_', ''));
      await storageService.eventos.actualizar(localId, updatedEvento);
      // Recargar eventos
      await get().loadEventos();
    } else {
      // Evento del backend
      const newEventos = eventos.map(e => e.id === id ? { ...e, ...updatedEvento } : e);
      set({ eventos: newEventos });
      saveEventos(newEventos);
    }
  },
  deleteEvento: async (id) => {
    const { eventos } = get();
    
    // Verificar si es un evento local
    if (id && id.toString().startsWith('local_')) {
      const localId = parseInt(id.toString().replace('local_', ''));
      await storageService.eventos.eliminar(localId);
      // Recargar eventos
      await get().loadEventos();
    } else {
      // Evento del backend
      const newEventos = eventos.filter(e => e.id !== id);
      set({ eventos: newEventos });
      saveEventos(newEventos);
    }
  },
  
  // Acciones - Settings
  updateSettings: (newSettings) => {
    const { settings } = get();
    set({ settings: { ...settings, ...newSettings } });
  },
  
  // Acciones - Filtros
  setFilters: (newFilters) => {
    const { filters } = get();
    set({ filters: { ...filters, ...newFilters } });
  },
  
  toggleShowCitas: () => {
    const { filters } = get();
    set({ filters: { ...filters, showCitas: !filters.showCitas } });
  },
  
  toggleShowLecturas: () => {
    const { filters } = get();
    set({ filters: { ...filters, showLecturas: !filters.showLecturas } });
  },
  
  toggleShowEventos: () => {
    const { filters } = get();
    set({ filters: { ...filters, showEventos: !filters.showEventos } });
  },
  
  toggleShowFestividadesJudias: () => {
    const { filters } = get();
    set({ filters: { ...filters, showFestividadesJudias: !filters.showFestividadesJudias } });
  },
  
  toggleTipoLectura: (tipo) => {
    const { filters } = get();
    const tiposLectura = filters.tiposLectura.includes(tipo)
      ? filters.tiposLectura.filter(t => t !== tipo)
      : [...filters.tiposLectura, tipo];
    set({ filters: { ...filters, tiposLectura } });
  },
  
  clearFilters: () => {
    set({ 
      filters: {
        showCitas: true,
        showLecturas: true,
        showEventos: true,
        showFestividadesJudias: false, // Por defecto desactivado
        tiposLectura: [],
        tiposCita: [],
        tiposEvento: [],
      }
    });
  },
  
  // Inicialización
  initialize: async () => {
    console.log('🚀 [STORE] ========== INICIALIZANDO STORE ==========');
    set({ loading: true });
    try {
      console.log('🚀 [STORE] Cargando datos en paralelo...');
      await Promise.all([
        get().loadCitas(),
        get().loadLecturas(),
        get().loadEventos(),
      ]);
      console.log('🚀 [STORE] ✅ Inicialización completada');
      const state = get();
      console.log('🚀 [STORE] 📊 Estado final:', {
        citas: state.citas.length,
        lecturas: state.lecturas.length,
        eventos: state.eventos.length,
        useBackend: state.useBackend
      });
    } catch (error) {
      console.error('🚀 [STORE] ❌ Error en inicialización:', error);
    } finally {
      set({ loading: false });
      console.log('🚀 [STORE] ========== FIN INICIALIZACIÓN ==========');
    }
  },
}));



